import { Dot } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import DocumentViewer from "./DocumentViewer";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../context/AuthContext";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import PDF from "./svg/Pdf";
import AsyncSelect from "react-select/async";
import dateIcon from "../../../../assets/images/dateIcon.svg";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import EditingRestrictionNotice from "./EditRestrictionNotice";
import { documentValidation } from "../employeesModals/validation";

const HealthCardNew = ({
  healthCardDetails,
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const [fileNames, setFileNames] = useState({
    frontDocument: healthCardDetails?.frontDocument || "",
    backDocument: healthCardDetails?.backDocument || "",
  });
  const [initialValues, setInitialValues] = useState({});
  const [healthCardError, setHealthCardError] = useState(null);
  const [editingSections, setEditingSections] = useState({
    healthCard: false,
    documents: false,
  });
  const [loading, setLoading] = useState(false);
  const { fileUpload, updateEmployeeRegistrationMaster, updateInsuranceInfo } =
    MainServices();

  const handleDocumentChange = (e, setFieldValue) => {
    const { name, files } = e.target;

    if (files && files[0]) {
      const fileData = {
        file: files[0],
        fileName: files[0].name,
      };

      setFileNames((prev) => ({
        ...prev,
        [name]: fileData,
      }));

      // Set the Formik field name correctly
      const fieldName =
        name === "frontDocument" ? "healthCardDocFront" : "healthCardDocBack";

      setFieldValue(fieldName, fileData);

      console.log("File uploaded:", files[0].name);
    } else {
      console.log("No file selected");
    }
  };

  const handleRemoveDocument = (documentType, setFieldValue) => {
    setFileNames((prev) => ({
      ...prev,
      [documentType]: "",
    }));

    if (documentType === "frontDocument") {
      fileInputRef1.current.value = "";
      setFieldValue("healthCardDocFront", "");
    } else if (documentType === "backDocument") {
      fileInputRef2.current.value = "";
      setFieldValue("healthCardDocBack", "");
    }
  };

  const handleViewDocument = (documentUrl, documentType, documentLabel) => {
    setSelectedDocument({
      url: documentUrl,
      type: getFileType(documentUrl),
      label: documentLabel,
    });
    setDocumentViewerOpen(true);
  };

  const getFileType = (url) => {
    try {
      const urlObj = new URL(url); // Ensure it's a valid URL
      const pathname = urlObj.pathname;
      const extension = pathname.split(".").pop().toLowerCase();

      return extension || "unknown"; // Return "unknown" if no extension found
    } catch (error) {
      return "invalid URL"; // Handle invalid URLs
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);

    const updatedHealthCardInfo = {
      healthCard: values.healthCard,
      ...{
        healthCardNo: values.healthCardNo,
      },
      ...{
        healthCardExpiry: values.healthCardExpiry,
      },
    };

    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };
    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      documents: {
        healthCardInfo: updatedHealthCardInfo,
      },
    };

    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        handleEditToggle("healthCard");
        setHealthCardError(null);
        toast("Health Card Details Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update designation", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocuments = async (values) => {
    if (healthCardDetails?.healthCard !== "true") {
      setHealthCardError(
        "Please enable Health Card and complete all required details before submitting documents"
      );
      return;
    } else if (
      !healthCardDetails?.healthCardNo ||
      !healthCardDetails?.healthCardExpiry
    ) {
      setHealthCardError(
        "Please complete all required Health Card details before submitting documents"
      );
      return;
    } else {
      setHealthCardError(null); // Clear error if details are complete
    }
    const files = [];

    // Add files only if they exist
    if (fileNames?.frontDocument?.file) {
      files.push(fileNames.frontDocument.file);
    }
    if (fileNames?.backDocument?.file) {
      files.push(fileNames.backDocument.file);
    }

    try {
      setLoading(true);
      let updatedFileNames = {};

      // If there are files to upload, call the fileUpload API
      if (files.length > 0) {
        const response = await fileUpload(files);
        if (response?.data) {
          const responseData = response?.data?.data;
          let currentIndex = 0;

          // Handle front document
          if (fileNames?.frontDocument?.file) {
            updatedFileNames.frontDocument =
              responseData[currentIndex]?.fileUrl;
            currentIndex++;
          } else {
            updatedFileNames.frontDocument = fileNames.frontDocument || ""; // Use existing value or empty
          }

          // Handle back document
          if (fileNames?.backDocument?.file) {
            updatedFileNames.backDocument = responseData[currentIndex]?.fileUrl;
          } else {
            updatedFileNames.backDocument = fileNames.backDocument || ""; // Use existing value or empty
          }
        } else {
          throw new Error(response?.message || "Unknown error");
        }
      } else {
        // If no files to upload, directly use the existing fileNames values
        updatedFileNames = {
          frontDocument: fileNames.frontDocument, // Use existing value or empty
          backDocument: fileNames.backDocument, // Use existing value or empty
        };
      }

      // Prepare the personal info object
      let formattedPersonalInfo = {
        ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
        ...(personalInfo?.secondName && {
          secondName: personalInfo.secondName,
        }),
        ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
        ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
        ...(personalInfo?.phone && { phone: personalInfo.phone }),
        ...(personalInfo?.email && { email: personalInfo.email }),
        ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
        ...(personalInfo?.dobGregorian && {
          dobGregorian: personalInfo.dobGregorian,
        }),
        ...(personalInfo?.religion && { religion: personalInfo.religion }),
        ...(personalInfo?.maritalStatus && {
          maritalStatus: personalInfo.maritalStatus,
        }),
        ...(personalInfo?.gender && { gender: personalInfo.gender }),
        ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

        ...(personalInfo?.citizen === "false" &&
          personalInfo?.nationality && {
            nationality: personalInfo?.nationality,
          }),
        ...(personalInfo?.citizen === "true" &&
          personalInfo?.nationalId && {
            nationalId: personalInfo.nationalId,
          }),
      };

      // Prepare the updated national ID info
      const updatedHealthCardInfo = {
        ...(values?.healthCard && {
          healthCard: values.healthCard,
        }),
        ...{
          healthCardNo: values.healthCardNo,
        },
        ...{
          healthCardExpiry: values.healthCardExpiry,
        },
        frontDocument: updatedFileNames.frontDocument,
        backDocument: updatedFileNames.backDocument,
      };

      // Prepare the final payload
      const details = {
        ...(formattedPersonalInfo && formattedPersonalInfo),
        documents: {
          healthCardInfo: updatedHealthCardInfo,
        },
      };

      // Call the update API
      const updateResponse = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (updateResponse?.data) {
        toast("Health Card Documents Updated Successfully!", {
          style: { zIndex: 999999999999999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
        // Toggle back to "Edit" mode
        handleEditToggle("documents");
      }
    } catch (error) {
      console.error("Error updating national ID information:", error);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const getValidationSchema = (section) => {
    const healthCardSchema = {
      healthCard: Yup.string().required("Health Card is required"),
      healthCardNo: Yup.string().when("healthCard", {
        is: "true",
        then: (schema) =>
          schema
            .required("Health Card Number is required")
            .matches(
              /^[\u0600-\u06FFA-Za-z0-9]+$/,
              "Only letters and numbers are allowed"
            )
            .min(5, "Health Card Number must be at least 5 characters")
            .max(20, "Health Card Number must not exceed 20 characters")
            .trim(),
        otherwise: (schema) =>
          schema.test(
            "empty-when-false",
            "Health Card Number should be empty when Health Card is No",
            function (value) {
              const { healthCard } = this.parent;
              if (healthCard === "false" && value && value.trim() !== "") {
                return false;
              }
              return true;
            }
          ),
      }),
      healthCardExpiry: Yup.string().when("healthCard", {
        is: "true",
        then: (schema) =>
          schema.required("Health Card Expiry is required").trim(),
        otherwise: (schema) =>
          schema.test(
            "empty-when-false",
            "Health Card Expiry should be empty when Health Card is No",
            function (value) {
              const { healthCard } = this.parent;
              if (healthCard === "false" && value && value.trim() !== "") {
                return false;
              }
              return true;
            }
          ),
      }),
    };

    const documentSchema = {
      healthCardDocFront: documentValidation(),
      healthCardDocBack: documentValidation(),
    };

    // Return based on section
    if (section === "healthCard") {
      return Yup.object().shape(healthCardSchema);
    } else if (section === "documents") {
      return Yup.object().shape(documentSchema);
    } else {
      return Yup.object().shape({
        ...healthCardSchema,
        ...documentSchema,
      });
    }
  };

  const getDocumentsValidation = () => {
    return Yup.object().shape({
      // healthCardDocFront: Yup.mixed().test(
      //   "healthCard-required",
      //   "Health Card selection is required to upload documents",
      //   function (_, context) {
      //     const { healthCard } = context.parent;
      //     if (healthCard === undefined || healthCard === null) return false; // Show error if healthCard is not selected
      //     if (healthCard === "false") return false; // Show error if healthCard is explicitly false
      //     return true; // Otherwise, allow upload
      //   }
      // ),
      // healthCardDocBack: Yup.mixed().test(
      //   "healthCard-required",
      //   "Health Card selection is required to upload documents",
      //   function (_, context) {
      //     const { healthCard } = context.parent;
      //     if (healthCard === undefined || healthCard === null) return false; // Show error if healthCard is not selected
      //     if (healthCard === "false") return false; // Show error if healthCard is explicitly false
      //     return true; // Otherwise, allow upload
      //   }
      // ),
    });
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  function handleOpenFileInput() {
    fileInputRef1.current.click();
  }

  function handleOpenFileInput2() {
    fileInputRef2.current.click();
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty values
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const calculateExpiryDays = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    if (isNaN(expiryDate)) return null;
    const diffInMs = expiryDate - new Date();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays > 0 ? `Expiry In ${diffInDays} Days` : "Expired";
  };

  const handleEditToggle = (section, values) => {
    setEditingSections((prev) => {
      const isCurrentlyOpen = prev[section];
      const newState = {
        healthCard: false,
        documents: false,
      };

      // If the clicked section was previously closed, open it
      if (!isCurrentlyOpen) {
        newState[section] = true;
      }

      return newState;
    });

    if (values) {
      setInitialValues(values);
    }
  };

  return (
    <>
      <div className="tab-section">
        {/* Iqama Section */}
        <Formik
          enableReinitialize
          initialValues={{
            healthCard: healthCardDetails?.healthCard || "",
            healthCardNo: healthCardDetails?.healthCardNo || "",
            healthCardExpiry: healthCardDetails?.healthCardExpiry || "",
            healthCardDocFront: healthCardDetails?.frontDocument || "",
            healthCardDocBack: healthCardDetails?.backDocument || "",
          }}
          validationSchema={
            editingSections.healthCard
              ? getValidationSchema("healthCard")
              : editingSections.documents
              ? getValidationSchema("documents")
              : undefined
          }
          onSubmit={(values) => {
            if (editingSections.healthCard) {
              handleSubmit(values);
            } else if (editingSections.documents) {
              handleUpdateDocuments(values);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, resetForm }) => (
            <>
              <div className="tab-header-container">
                <h3 className="tab-heading">{t("Health Card")}</h3>
                {currentEmployeeEditing ? (
                  <EditingRestrictionNotice message="Read-only" />
                ) : (
                  !editingSections.healthCard && (
                    <div
                      className="tab-header-edit-container"
                      onClick={() => handleEditToggle("healthCard", values)}
                    >
                      <img src={Edit} alt="edit icon" />
                      <span className="edit-txt">{t("Edit")}</span>
                    </div>
                  )
                )}
              </div>
              <Form className="form-section">
                <div className="form-group">
                  <div className="add-body-input-section">
                    <div className="add-label-container">
                      <label className="add-label">{t("Health Card")}</label>
                      <span className="upload-req-mark">*</span>
                    </div>
                    {editingSections.healthCard ? (
                      <div className="add-radio-input-container">
                        <div className="add-radio-container">
                          <input
                            id="yes"
                            type="radio"
                            name="healthCard"
                            className="add-radio-input"
                            value="true"
                            onChange={(e) =>
                              setFieldValue("healthCard", e.target.value)
                            }
                            checked={values?.healthCard === "true"}
                          />
                          <label htmlFor="yes" className="add-label">
                            {t("Yes")}
                          </label>
                        </div>
                        <div className="add-radio-container">
                          <input
                            id="no"
                            type="radio"
                            name="healthCard"
                            className="add-radio-input"
                            value="false"
                            onChange={(e) =>
                              setFieldValue("healthCard", e.target.value)
                            }
                            checked={values?.healthCard === "false"}
                          />
                          <label htmlFor="no" className="add-label">
                            {t("No")}
                          </label>
                        </div>
                      </div>
                    ) : (
                      <div className="add-radio-input-container">
                        <span>
                          {values?.healthCard === "true" ? "Yes" : "No"}
                        </span>
                      </div>
                    )}
                  </div>
                  <CustomErrorMessage
                    name="healthCard"
                    errors={errors}
                    touched={touched}
                  />
                </div>

                <div className="add-body-content-flex">
                  <div className="add-body-content-grid">
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label className="add-label" htmlFor="healthCardNo">
                            {t("Health Card Number")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {editingSections.healthCard ? (
                          <div className="add-input-wrapper">
                            <input
                              id="healthCardNo"
                              name="healthCardNo"
                              className="add-input"
                              placeholder={t("Enter Health Card Number")}
                              value={values?.healthCardNo}
                              onChange={(e) =>
                                setFieldValue("healthCardNo", e.target.value)
                              }
                            />
                          </div>
                        ) : (
                          <div className="add-input-wrapper">
                            <span>{values?.healthCardNo || t("N/A")}</span>
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="healthCardNo"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                    <div className="form-group">
                      <div className="add-body-input-section">
                        <div className="add-label-container">
                          <label
                            className="add-label"
                            htmlFor="healthCardExpiry"
                          >
                            {t("Health Card Expiry")}
                          </label>
                          <span className="add-req-mark">*</span>
                        </div>
                        {editingSections.healthCard ? (
                          <div className="add-input-wrapper">
                            <input
                              type="date"
                              id="healthCardExpiry"
                              name="healthCardExpiry"
                              className={`add-wrap-input ${
                                !values?.healthCardExpiry ||
                                values.healthCardExpiry === "dd-mm-yyyy"
                                  ? "empty"
                                  : ""
                              }`}
                              placeholder="DD/MM/YYYY"
                              value={
                                values?.healthCardExpiry
                                  ? formatDateToYYYYMMDD(
                                      values.healthCardExpiry
                                    )
                                  : ""
                              }
                              onChange={(e) =>
                                setFieldValue(
                                  "healthCardExpiry",
                                  e.target.value
                                )
                              }
                            />
                            <img
                              src={dateIcon}
                              alt="date icon"
                              className="add-date-input-in"
                            />
                          </div>
                        ) : (
                          <div className="add-input-wrapper">
                            <span>
                              {formatDateToYYYYMMDD(values?.healthCardExpiry)}
                            </span>
                          </div>
                        )}
                      </div>
                      <CustomErrorMessage
                        name="healthCardExpiry"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                </div>

                {/* Submit button for Health Card section (only visible in edit mode) */}
                {editingSections.healthCard && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          handleEditToggle("healthCard");
                        }}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Documents Section Header */}
                <div className="tab-header-container">
                  <h3 className="tab-heading">{t("Documents")}</h3>
                  {currentEmployeeEditing ? (
                    <EditingRestrictionNotice message="View-only" />
                  ) : (
                    !editingSections.documents && (
                      <div
                        className="tab-header-edit-container"
                        onClick={() => handleEditToggle("documents")}
                      >
                        <img src={Edit} alt="edit icon" />
                        <span className="edit-txt">{t("Edit")}</span>
                      </div>
                    )
                  )}
                </div>

                {/* Documents Section Body */}
                {editingSections.documents ? (
                  // Edit Mode
                  <div className="upload-body-tab">
                    {healthCardError && (
                      <div className="details-error">{healthCardError}</div>
                    )}
                    {/* Front Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="healthCardDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames.frontDocument.fileName ||
                                fileNames.frontDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="frontDocument"
                              ref={fileInputRef1}
                              id="healthCardDocFront"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.frontDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "frontDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="healthCardDocFront"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="healthCardDocBack"
                            className="upload-label"
                          >
                            {t("Back Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput2}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames.backDocument.fileName ||
                                fileNames.backDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="backDocument"
                              ref={fileInputRef2}
                              id="healthCardDocBack"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.backDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "backDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="healthCardDocBack"
                        errors={errors}
                        touched={touched}
                      />
                      <CustomErrorMessage
                        name="healthCard"
                        errors={errors}
                        touched={touched}
                      />
                      <CustomErrorMessage
                        name="healthCardNo"
                        errors={errors}
                        touched={touched}
                      />
                      <CustomErrorMessage
                        name="healthCardExpiry"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                ) : (
                  // Non-Edit Mode
                  <div className="upload-body-tab">
                    {/* Front Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="healthCardDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.healthCardDocFront
                                ? t("Front Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.healthCardDocFront && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.healthCardDocFront,
                                  "Front Document",
                                  "Health Card Front"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="healthCardDocBack"
                            className="upload-label"
                          >
                            {t("Back Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.healthCardDocBack
                                ? t("Back Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.healthCardDocBack && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.healthCardDocBack,
                                  "Back Document",
                                  "Health Card Back"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit button for Documents section (only visible in edit mode) */}
                {editingSections.documents && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => handleEditToggle("documents")}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      </div>

      <DocumentViewer
        isOpen={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={selectedDocument?.url}
        documentType={selectedDocument?.type}
        documentLabel={selectedDocument?.label}
      />
    </>
  );
};

export default HealthCardNew;
